"use client";

import { useRef, useState, useEffect } from "react";
import { useParams, useRouter } from "next/navigation";
import ValidatedField from "@/app/[locale]/auth/login/components/UI/ValidatedField";
import validateToken from "@/lib/auth/validateToken";
import validatePassword from "@/lib/auth/validatePassword";
import resetPassword from "@/lib/auth/resetPassword";
import AlertBox from "@/app/[locale]/auth/login/components/UI/AlertBox";
import { SwitchCallback } from "@/app/[locale]/auth/login/components/types";

type Props = {
  onResetCompleted: SwitchCallback;
};

export default function ResetPasswordConfirmForm({ onResetCompleted }: Props) {
  const formRef = useRef<HTMLFormElement>(null);
  const { token } = useParams<{ token: string }>();
  const router = useRouter();

  const [password, setPassword] = useState("");
  const [confirmedPassword, setConfirmedPassword] = useState("");
  const [passwordValidationError, setPasswordValidationError] = useState("");
  const [
    confirmedPasswordValidationError,
    setConfirmedPasswordValidationError,
  ] = useState("");
  const [formError, setFormError] = useState("");
  const [hasFatalError, setHasFatalError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validating, setIsValidating] = useState(true);

  // 1. Validate token on mount
  useEffect(() => {
    if (!token) return;
    validateToken(token as string)
      .then((res) => {
        setIsValidating(false);
        if (!res.is_valid) {
          setFormError("This password reset link is invalid or expired.");
          setHasFatalError(true);
        }
      })
      .catch(() => {
        setIsValidating(false);
        setFormError(
          "There was a problem validating your reset link. You may try again by refreshing this form.",
        );
        setHasFatalError(true);
      });
  }, [token]);

  // 2. Validate password on blur/change
  const validatePasswordAndConfirmation = async (
    inPassword: string,
    isConfirmation: boolean,
  ) => {
    if (!isConfirmation) {
      const error = await validatePassword({ password: inPassword });
      setPasswordValidationError(error || "");
    }

    if (isConfirmation) {
      if (inPassword !== password) {
        setConfirmedPasswordValidationError("Passwords do not match");
      } else {
        setConfirmedPasswordValidationError("");
      }
    }
  };

  // 3. On submit, reset password
  async function handleConfirmPassword(e: React.FormEvent) {
    e.preventDefault();
    setFormError("");

    // Validate before submit
    const passwordError = await validatePassword({ password });
    setPasswordValidationError(passwordError || "");
    if (passwordError) return;

    if (password !== confirmedPassword) {
      setConfirmedPasswordValidationError("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      const resp = await resetPassword(
        { new_password1: password, new_password2: confirmedPassword },
        token as string,
      );

      if (resp.reset_status) {
        onResetCompleted(undefined);
      } else {
        if (resp.token_invalid) {
          setFormError("This password reset link is invalid or expired.");
          setHasFatalError(true);
        } else {
          setFormError(
            resp.err_msg
              ? resp.err_msg
              : "Failed to reset password. Please try again.",
          );
        }
        setHasFatalError(false);
      }
    } catch (err: any) {
      setFormError("Failed to reset password. Please try again.");
      setHasFatalError(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-[464px] mb-6 bg-white">
      <h1 className="font-bold text-2xl mb-5 text-blue-900">Reset Password</h1>

      {validating ? (
        <AlertBox
          message="Validating supplied token. Please wait."
          type="info"
        />
      ) : (
        <>
          {formError && <AlertBox type="error" message={formError} />}

          {!hasFatalError && (
            <>
              <p className="mb-6">Enter and confirm your new password.</p>
              <form onSubmit={handleConfirmPassword} ref={formRef}>
                <ValidatedField
                  label="Password"
                  name="password"
                  autoComplete="new-password"
                  onChange={setPassword}
                  onBlur={(e) =>
                    validatePasswordAndConfirmation(e.target.value, false)
                  }
                  validationError={passwordValidationError}
                  value={password}
                />
                <ValidatedField
                  label="Confirm Password"
                  name="confirm_password"
                  autoComplete="new-password"
                  onChange={setConfirmedPassword}
                  onBlur={(e) =>
                    validatePasswordAndConfirmation(e.target.value, true)
                  }
                  validationError={confirmedPasswordValidationError}
                  value={confirmedPassword}
                />
                <button
                  type="submit"
                  className="my-6 bg-danger-500 hover:bg-danger-700 text-white py-2 px-4 text-base rounded cursor-pointer border-0 transition-colors w-full"
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </form>
            </>
          )}
        </>
      )}
    </div>
  );
}
